<template>
    <div class="leonardo-wrapper">
        <v-dialog />

        <app-navigation
            ref="navigation"
            :images="images"
            :plexipeople="plexipeople"
            :enableButtons="!(isInAppBrowser || showBrowserPreloadingSplash || extractorOpen || showIntroVideo)"
            @openExtractor="openExtractor"
            @addImage="addImage"
            @completeImage="completeImage"
            @startIntro="startIntro('navigation', true)"
            @remove="removeExtraction"
            @edit="openExtractor"
        />

        <template v-if="isInAppBrowser">
            <in-app-browser-tutorial />
        </template>
        <template v-else>
            <app-loading-splash v-if="loadingSplashOpen" />

            <div
                v-if="showIntroVideo"
                class="is--intro-video"
            >
                <div id="player" :class="{'show--border': videoLoaded}" ref="player">
                    <div v-if="introVideoShown" class="is--after-video-uploader" @click="closeIntroVideo" :style="afterVideoLayerStyle">
                        <img
                            src="/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/img/replay-video.png"
                            alt="Replay"
                            @click.stop="initIntroVideo"
                        >
                    </div>
                </div>

                <div class="is-actions">
                    <button
                        class="btn is--primary is--center"
                        @click="closeIntroVideo"
                    >
                        {{ $t('introVideo.uploadButton') }}
                    </button>
                    <div class="uploadhint">(JPG,BMP,PNG,TIFF, max. 25 MB)</div>
                </div>
            </div>

            <div class="is--netiplexipeopleleonardo" v-if="!showIntroVideo">
                <template v-if="showBrowserPreloadingSplash">
                    <div class="preloading-images-indicator mx-auto">
                        <img
                            src="/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/img/loading.svg"
                            alt="Loading"
                        >
                    </div>
                </template>

                <div class="content--upload" v-if="!showBrowserPreloadingSplash && extractorOpen && !loadingSplashOpen">
                    <app-extractor
                        @addExtraction="addExtraction"
                        @addToEditor="addToEditor"
                        @openExtractor="openExtractor"
                        @close="closeExtractor"
                        :viewport="viewport"
                        :config="extractor"
                        :preview="preview"
                        :availableHeight="availableHeight"
                    />
                </div>

                <div class="is--flex mx-2" v-if="!(showBrowserPreloadingSplash || loadingSplashOpen || imagesOpen || extractorOpen)">
                    <praise name="praise"
                            :class="{ 'is--show': openPraise, 'is--out': ! openPraise }"
                            @closePraise="closePraise"
                            transition="scale"
                            :height="'100%'"
                            :width="'100%'">
                    </praise>
                    <div class="product--image-container">
                        <app-editor
                            v-if="!(imagesOpen || extractorOpen)"
                            @viewportFactorChanged="setViewportFactor"
                            @activated="setActivePlexipeople"
                            @deactivated="deactivePlexipeople"
                            @preloaderDone="preloaderDone"
                            @mounted="restoreDataFromRecord"
                            :viewport="viewport"
                            :plexipeople="plexipeople"
                            :base="base"
                            :is-loading="loadingSplashOpen"
                            :preload-active="showPreloader"
                        >
                        </app-editor>
                    </div>

                    <div class="product--buybox hide--mobile">
                        <app-price-list
                            :base="base"
                            :plexipeople="plexipeople"
                            :viewport="viewport"
                            :activePlexipeople="visualization.plexipeople"
                            @activatePlexipeople="activatePlexipeople"
                            @baseWidthChanged="setBaseWidth"
                            @priceUpdated="setPrice"
                        >
                        </app-price-list>

                        <div v-if="!viewport.isMobile" class="content--title mb-1">{{ $t("editor.chooseBaseHeadline") }}
                        </div>

                        <app-base
                            :base="base"
                            :plexipeople="plexipeople"
                            :viewport="viewport"
                        >
                        </app-base>

                        <div class="buybox--container" v-if="! (loadingSplashOpen || imagesOpen || extractorOpen)">
                            <div v-if="!viewport.isMobile" class="price--container">
                                <div class="product--price">
                                    <span class="price--label"></span>Dein Preis
                                    <span class="price--content">{{ price }}*</span><br>
                                    <span class="price--content" style="font-size: 12px">inkl. MwSt. zzgl. Versandkosten</span>
                                </div>
                                <div class="product--delivery">
                                    <p class="delivery--information">
                                        <span class="delivery--text delivery--text-not-available"><i class="delivery--status-icon delivery--status-not-available"></i>Lieferzeit: <span style="color:red;">Keine garantierte <br>Lieferung vor dem Valentinstag </span></span>
                                    </p>
                                </div>
                            </div>
                            <app-buy-button
                                :base="base"
                                :plexipeople="plexipeople"
                                :viewport="viewport"
                                :price="price"
                                :quantity="quantity"
                                :record="record"
                                @quantity-changed="updateQuantity"
                            >
                            </app-buy-button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import EventBus from './event-bus';
    import ShapesConfiguration from './shapes-configuration';
    import MobileDetect from 'mobile-detect';
    import InAppBrowserTutorial from "./components/InAppBrowserTurorial";
    import AppLoadingSplash from './components/LoadingSplash';
    import AppNavigation from './components/Navigation';
    import AppExtractor from './components/ClipPath';
    import AppEditor from './components/Editor';
    import AppPlexipeopleVisualization from './components/PlexipeopleVisualization'
    import AppPriceList from './components/PriceList';
    import AppBase from './components/Base';
    import AppBuyButton from './components/BuyButton';
    import RandomColor from 'randomcolor';
    import { calcMaxDimensions, generateRandomID } from './utilities';
    import Player from "@vimeo/player";
    import Praise from "./components/Praise";

    export default {
        data() {
            let isMobile = null !== (new MobileDetect(window.navigator.userAgent)).mobile(),
                variants = App.configuration.variants;

            const { record = false } = App;

            const isInAppBrowser = (function() {
                let ua = navigator.userAgent || navigator.vendor || window.opera;

                let isIos = 'iOS' === (new MobileDetect(window.navigator.userAgent)).os();
                if (isIos === true) {
                    return false;
                }
                return (
                        (ua.indexOf('Instagram') > -1)
                        || (ua.indexOf('Pinterest') > -1)
                );
            })();

            return {
                isInAppBrowser,
                record,
                showBrowserPreloadingSplash: false,
                // showIntroVideo: ! (this.$cookies.get('introVideoShown') || false),
                showIntroVideo: !record,
                restoreable: false,
                player: false,
                videoLoaded: false,
                introVideoShown: false,
                introJsShown: this.$cookies.get('introJsShown') || {},
                //introJsShown: {},
                introJsInstance: false,
                introJsMessages: [],
                leavingEditorEventListenerRegistered: false,
                showAutoWidthDialog: false,
                loadingSplashOpen: true,
                showPreloader: false,
                extractorOpen: false,
                imagesOpen: false,
                images: [],
                preview: {
                    'max-height': '150px'
                },

                extractor: {
                    editInPlace: false,
                    target: false,
                    images: [],
                    coords: [],
                    showModalOnClose: false
                },

                viewport: {
                    isMobile: isMobile,
                    width: window.innerWidth,
                    height: window.innerHeight,
                    factor: 20,
                    format: 'portrait',

                    trigger: [
                        {
                            indicator: 'S',
                            label: '15 x 18 cm',
                            value: {
                                x: 150,
                                y: 180
                            },
                            line: {
                                width: (100 / (30 / 15)) + '%',
                                height: (100 / (30 / 18)) + '%'
                            },
                            active: false
                        },
                        {
                            indicator: 'M',
                            label: '22 x 25 cm',
                            value: {
                                x: 220,
                                y: 250
                            },
                            line: {
                                width: (100 / (30 / 22)) + '%',
                                height: (100 / (30 / 25)) + '%'
                            },
                            active: false
                        },
                        {
                            indicator: 'L',
                            label: '30 x 30 cm',
                            value: {
                                x: 300,
                                y: 300
                            },
                            line: {
                                width: (100 / (30 / 30)) + '%',
                                height: (100 / (30 / 30)) + '%'
                            },
                            active: false
                        }
                    ]
                },

                availableHeight: 0,

                base: {
                    autoWidth: true,
                    color: variants.colors.elements[variants.colors.selected || 0],
                    grooves: variants.grooves.elements[variants.grooves.selected || 0],
                    height: {
                        value: 20
                    },
                    material: variants.materials.elements[variants.materials.selected || 0],
                    width: variants.widths.elements[variants.widths.selected || 0]
                },

                plexipeople: [],

                visualization: {
                    plexipeople: {},
                },

                quantity: 1,

                price: .0,
                openPraise: false,
            };
        },

        computed: {
            afterVideoLayerStyle() {
                let size = this.getIntroVideoSize() + "px";

                return {
                    width: size,
                    height: size,
                };
            },
        },

        methods: {
            openBrowserPeloadingSplash() {
                this.showBrowserPreloadingSplash = true;
            },

            closeBrowserPeloadingSplash() {
                this.showBrowserPreloadingSplash = false;
            },

            preloaderDone() {
                this.showPreloader = false;
                this.startIntroDelayedBy('navigation', 1000, false);
            },

            closeIntroVideo() {
                this.$cookies.set('introVideoShown', !(this.showIntroVideo = false));
                EventBus.$emit('navigation_triggerUploadButton');
            },

            updateQuantity(quantity) {
                this.quantity = quantity;
            },

            setBaseWidth(id) {
                let i = 0;

                for (; i < App.configuration.variants.widths.elements.length; i++) {
                    if (id !== App.configuration.variants.widths.elements[i].id) {
                        continue;
                    }

                    this.base.width = App.configuration.variants.widths.elements[i];

                    if (! this.showAutoWidthDialog) {
                        return;
                    }

                    this.showAutoWidthDialog = false;

                    this.$modal.show('dialog', {
                        title: this._i18n.t('hint.autoWidth.headline'),
                        text: this._i18n.t('hint.autoWidth.body') + '<img src="/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/img/hint-auto-width.png" />',
                        buttons: [
                            {
                                title: this._i18n.t('hint.autoWidth.confirm')
                            }
                        ]
                    });

                    return;
                }
            },

            setPrice(price) {
                this.price = price;
            },

            resetActivePlexipeople() {
                this.visualization.plexipeople = {};
            },

            setActivePlexipeople(plexipeople) {
                let i = 0;

                for (; i < this.plexipeople.length; i++) {
                    if (this.plexipeople[i].uuid !== plexipeople.uuid) {
                        continue;
                    }

                    this.visualization.plexipeople = this.plexipeople[i];
                    this.viewport.format           = plexipeople.format;
                }
            },

            activatePlexipeople(plexipeople) {
                let i = 0, match, undef;

                for (; i < this.plexipeople.length; i++) {
                    if (this.plexipeople[i].uuid !== plexipeople.uuid) {
                        this.plexipeople[i].active = false;

                        continue;
                    }

                    match                      = this.plexipeople[i];
                    this.plexipeople[i].active = true;
                }

                if (undef === match) {
                    this.resetActivePlexipeople();
                }

                this.setActivePlexipeople(match);
            },

            deactivePlexipeople(plexipeople) {
                if (this.visualization.plexipeople.uuid === plexipeople.uuid) {
                    this.resetActivePlexipeople();
                }
            },

            resizePlexipeople(plexipeople, size) {
                let i = 0;

                for (; i < this.plexipeople.length; i++) {
                    if (this.plexipeople[i].uuid !== plexipeople.uuid) {
                        continue;
                    }

                    let isPortrait = this.plexipeople[i].format !== 'panorama',
                        dim        = calcMaxDimensions(
                            this.plexipeople[i].extraction.geometry.width,
                            this.plexipeople[i].extraction.geometry.height,
                            isPortrait ? size.value.x : size.value.y,
                            isPortrait ? size.value.y : size.value.x
                        );

                    this.plexipeople[i].width  = dim.x / 10 * this.viewport.factor;
                    this.plexipeople[i].height = dim.y / 10 * this.viewport.factor;

                    let viewportWidth = 30 * this.viewport.factor;

                    if (1 === this.plexipeople.length) {
                        this.plexipeople[i].x = (viewportWidth - this.plexipeople[i].width) / 2;
                    } else if (viewportWidth < this.plexipeople[i].x + this.plexipeople[i].width) {
                        this.plexipeople[i].x = viewportWidth - this.plexipeople[i].width;
                    }

                    EventBus.$emit('calculatePrices');

                    return;
                }
            },

            closeExtractor(openEditor) {
                this.imagesOpen            = ! (openEditor || false);
                this.showPreloader         = openEditor || false;
                this.extractor.images      = [];
                this.extractor.editInPlace = false;
                this.extractorOpen         = false;

                if (! (this.extractor.showModalOnClose && openEditor)) {
                    return;
                }

                this.extractor.showModalOnClose = false;

                EventBus.$emit('calculatePrices');
            },

            openExtractor(images, extraction, editInPlace) {
                let undef;

                if ('_uploaded' === images) {
                    images = [];

                    this.images.forEach((image) => {
                        if (0 === image.extractions.length) {
                            images.push(image);
                        }
                    });
                }

                if (0 === this.images.length) {
                    return;
                }

                if (undef === images) {
                    images     = [ this.visualization.plexipeople.extraction.parent ];
                    extraction = this.visualization.plexipeople.extraction;
                }

                editInPlace = editInPlace || false;

                this.extractor.images      = images;
                this.extractor.editInPlace = editInPlace;
                this.extractor.target      = extraction || false;
                this.extractor.coords      = JSON.parse(JSON.stringify(
                    extraction
                        ? extraction.coords
                        : ShapesConfiguration[0].coords
                ));

                if (this.extractorOpen) {
                    EventBus.$emit('Handles:reset', this.extractor.coords);
                }

                this.extractorOpen = true;
            },

            addImage(file) {
                this.images.push({
                    completeTimestamp: file.completeTimestamp,
                    source: file.leonardo.fileUrl,
                    origin: file.leonardo.originUrl || '',
                    info: file.leonardo.info,
                    extractions: [],
                    uuid: file.uuid || generateRandomID(),
                });
            },

            completeImage(file) {
                let image = this.images.find(img => img.uuid === file.uuid);

                image.completeTimestamp = file.completeTimestamp;
                image.source            = file.leonardo.fileUrl;
                image.origin            = file.leonardo.originUrl || "";
                image.info              = file.leonardo.info || "";
            },

            addExtraction(extraction) {
                let image = extraction.parent, i;

                for (i = 0; i < this.images.length; i++) {
                    if (image !== this.images[i]) {
                        continue;
                    }

                    this.images[i].extractions = [ extraction ];
                }
            },

            removeExtraction(key) {
                let undef;

                if (undef === key) {
                    this.plexipeople.forEach((plexipeople, pKey) => {
                        if (plexipeople === this.visualization.plexipeople) {
                            key = pKey;
                        }
                    });
                }

                if (undef === key) {
                    return;
                }

                let _remove = () => {
                    let uuid = this.plexipeople[key].extraction.parent.uuid;

                    this.deactivePlexipeople(this.plexipeople[key]);
                    this.plexipeople.splice(key, 1);

                    if (0 < this.plexipeople.length) {
                        this.activatePlexipeople(this.plexipeople[0]);
                    }

                    this.images.forEach((image, key) => {
                        if (image.uuid === uuid) {
                            this.images.splice(key, 1);
                        }
                    });

                    EventBus.$emit('calculatePrices');
                };

                this.$modal.show('dialog', {
                    title: this._i18n.t('editor.remove-dialog.title'),
                    text: this._i18n.t('editor.remove-dialog.text'),
                    buttons: [
                        {
                            title: this._i18n.t('editor.remove-dialog.confirm'),
                            handler: () => {
                                _remove();
                                this.$modal.hide('dialog');
                            },
                            class: 'vue-dialog-button is--error',
                        },
                        {
                            title: this._i18n.t('editor.remove-dialog.cancel'),
                            class: 'vue-dialog-button is--success',
                        }
                    ]
                });
            },

            addToEditor(extraction, preset, target) {
                let vm = this, image = new Image();

                preset = preset || { groove: 1, x: false, y: false, width: false, height: false };

                image.addEventListener('load', function() {
                    let i         = 0,
                        maxWidth  = (vm.viewport.trigger[0].value.x / 10) * vm.viewport.factor,
                        maxHeight = (vm.viewport.trigger[0].value.y / 10) * vm.viewport.factor;

                    if (false !== target) {
                        for (; i < vm.plexipeople.length; i++) {
                            if (vm.plexipeople[i].extraction === target) {
                                maxWidth  = vm.plexipeople[i].width;
                                maxHeight = vm.plexipeople[i].height;
                                preset.x  = vm.plexipeople[i].x;

                                vm.plexipeople[i].extraction.inUse = false;
                                vm.plexipeople.splice(i, 1);

                                break;
                            }
                        }
                    }

                    let plexipeople,
                        format     = image.width > image.height ? 'panorama' : 'portrait',
                        isPortrait = format !== 'panorama',
                        dim        = calcMaxDimensions(
                            image.width,
                            image.height,
                            isPortrait ? maxWidth : maxHeight,
                            isPortrait ? maxHeight : maxWidth
                        );

                    plexipeople = {
                        extraction,
                        active: false,
                        groove: 1,
                        x: preset.x || (((30 * vm.viewport.factor) - dim.x) / 2),
                        y: preset.y || 0,
                        width: preset.width || dim.x,
                        height: preset.height || dim.y,
                        uuid: generateRandomID(),
                        indicator: RandomColor(),
                        size: preset.size || 'S'
                    };

                    plexipeople.format = format;

                    vm.plexipeople.push(plexipeople);

                    extraction.inUse = true;

                    EventBus.$emit('calculatePrices');

                    vm.activatePlexipeople(plexipeople);
                });

                image.src = extraction.source;
                vm.openPraise = true;
                console.log('praise');
            },

            openEditor() {
                this.closeExtractor(true);
            },

            findBaseElementById(element, id) {
                let elements = App.configuration.variants[element].elements, i;

                for (i in elements) {
                    if (elements[i].id === id) {
                        return elements[i];
                    }
                }
            },

            disableAutoWidth() {
                this.base.autoWidth = false;
            },

            restoreDataFromRecord() {
                let vm = this;

                if (!vm.restoreable) {
                    return;
                }

                vm.$http.post(App.url.loadRecordData, { record: vm.record }).then(
                    // Successful
                    response => {
                        vm.disableAutoWidth();

                        let restoreData     = JSON.parse(response.data.leonardoData),
                            imageCollection = [], i;

                        vm.closeExtractor();

                        restoreData.plexipeople.forEach(plexipeopleEntry => {
                            let extraction, image, plexipeople;

                            extraction = JSON.parse(JSON.stringify(plexipeopleEntry.extraction));

                            if (imageCollection[plexipeopleEntry.extraction.parent.uuid]) {
                                image = imageCollection[plexipeopleEntry.extraction.parent.uuid];
                            } else {
                                image             = JSON.parse(JSON.stringify(plexipeopleEntry.extraction.parent));
                                image.extractions = [];

                                imageCollection[plexipeopleEntry.extraction.parent.uuid] = image;
                            }

                            extraction.parent = image;
                            image.extractions.push(extraction);

                            plexipeople = JSON.parse(JSON.stringify(plexipeopleEntry));

                            let x = vm.viewport.width < plexipeople.x * vm.viewport.factor
                                    ? false
                                    : plexipeople.x * vm.viewport.factor;

                            vm.addToEditor(extraction, {
                                groove: 1,
                                x,
                                width: (plexipeople.width / 10) * vm.viewport.factor,
                                height: (plexipeople.height / 10) * vm.viewport.factor,
                            });
                        });

                        for (i in imageCollection) {
                            vm.images.push(imageCollection[i]);
                        }

                        vm.base.color    = vm.findBaseElementById('colors', restoreData.base.color.value.id);
                        vm.base.grooves  = vm.findBaseElementById('grooves', restoreData.base.grooves.value.id);
                        vm.base.material = vm.findBaseElementById('materials', restoreData.base.material.value.id);
                        vm.base.width    = vm.findBaseElementById('widths', restoreData.base.width.value.id);

                        vm.loadingSplashOpen = false;
                        vm.restoreable       = false;

                        vm.openEditor();

                        EventBus.$emit('calculatePrices');
                    },
                    response => {
                        vm.record            = false;
                        vm.loadingSplashOpen = false;
                        vm.showPreloader     = false;
                        vm.restoreable       = false;

                        vm.$modal.show('dialog', {
                            title: vm._i18n.t('error.restore.label'),
                            text: response.data.error || vm._i18n.t('error.restore.body'),
                            buttons: [
                                {
                                    title: vm._i18n.t('error.restore.confirm')
                                }
                            ]
                        });
                    }
                );
            },

            displayLeavingEditorWarning(event) {
                if (0 === this.plexipeople.length && 0 === this.images.length) {
                    return false;
                }

                event.preventDefault();

                return event.returnValue = this._i18n.t('app.leavingEditorWarning');
            },

            addLeavingEditorEventListener() {
                if (true === this.leavingEditorEventListenerRegistered) {
                    return;
                }

                window.addEventListener('beforeunload', this.displayLeavingEditorWarning);
                this.leavingEditorEventListenerRegistered = true;
            },

            removeLeavingEditorEventListener() {
                if (false === this.leavingEditorEventListenerRegistered) {
                    return;
                }

                window.removeEventListener('beforeunload', this.displayLeavingEditorWarning);
                this.leavingEditorEventListenerRegistered = false;
            },

            onResize(event) {
                let oldWidth  = this.viewport.width,
                    oldHeight = this.viewport.height;

                this.setViewportWidth(event.target.innerWidth);
                this.setViewportHeight(event.target.innerHeight);

                EventBus.$emit('resized', {
                    oldWidth,
                    oldHeight,
                    newWidth: this.viewport.width,
                    newHeight: this.viewport.height,
                });
            },

            setViewportWidth(width) {
                this.viewport.width = width;
            },

            setViewportHeight(height) {
                this.viewport.height = height;
            },

            setViewportFactor(factor) {
                this.viewport.factor = factor;
            },
            closePraise() {
                this.openPraise = false;
            },

            startIntroDelayedBy(view, delay = 1000, force = false, blockNextCall = true) {
                let vm = this;

                window.setTimeout(() => vm.startIntro(view, force, blockNextCall), delay);
            },

            startIntro(view, force = false, blockNextCall = true) {
                let previousState = this.introJsShown[view] || 0;

                if (! force && 1 === previousState) {
                    return;
                }

                this.introJsShown[view] = blockNextCall ? 1 : 0;
                this.introJsInstance    = this.$intro();
                this.introJsMessages    = [];

                this.introJsInstance.setOptions({
                    exitOnOverlayClick: false,
                    disableInteraction: !this.extractorOpen,
                    nextLabel: 'Weiter',
                    prevLabel: 'Zurück',
                    skipLabel: 'Skip',
                    doneLabel: 'Ok',
                    hidePrev: true,
                    hideNext: true,
                    tooltipPosition: "bottom-middle",
                });

                EventBus.$emit('collectIntroJsSteps', {
                    view,
                    instance: this.introJsMessages,
                });

                this.introJsMessages.sort((a, b) => (a.step > b.step) ? 1 : ((b.step > a.step) ? -1 : 0));
                this.introJsInstance.addSteps(this.introJsMessages);
                this.introJsInstance.start();

                this.$cookies.set('introJsShown', JSON.stringify(this.introJsShown),'7d');
            },

            getIntroVideoSize() {
                let style  = window.getComputedStyle(this.$refs["player"], null),
                    size   = Math.round(parseFloat(style.getPropertyValue("height")));

                if (size > this.viewport.width) {
                    size = this.viewport.width;
                }

                return size - 20;
            },

            initIntroVideo() {
                this.introVideoShown = false;

                let size = this.getIntroVideoSize();

                this.player = new Player('player', {
                        id: "366764812",
                        width: size,
                        controls: false,
                    });

                this.player.element.style.width  = size + "px";
                this.player.element.style.height = size + "px";

                this.player.setVolume(0);
                this.player.play();

                this.player.on("loaded", () => {
                    this.videoLoaded = true;
                });

                this.player.on("ended", () => {
                    this.introVideoShown = true;
                    this.videoLoaded     = false;

                    this.player.destroy();
                });
            },
        },

        beforeCreate() {
            let { beforeCreate } = App.events;

            if ("function" === typeof beforeCreate) {
                beforeCreate();
            }

            (new Image()).src = "/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/img/loading.svg";
        },

        created() {
            let { created } = App.events;

            if ("function" === typeof created) {
                created();
            }

            window.addEventListener('resize', this.onResize);
            this.addLeavingEditorEventListener();
            document.body.style['overscroll-behavior'] = 'contain';
        },

        beforeMount() {
            let { beforeMount } = App.events;

            if ("function" === typeof beforeMount) {
                beforeMount();
            }
        },

        mounted() {
            let { mounted } = App.events;

            if ("function" === typeof mounted) {
                mounted();
            }

            this.setViewportWidth(window.innerWidth);

            // console.log({
            //     'this.viewport.height': this.viewport.height,
            //     'this.$refs[\'navigation\'].$el': this.$refs['navigation'].$el,
            //     'this.$refs[\'navigation\'].$el.offsetHeight': this.$refs['navigation'].$el.offsetHeight,
            //     'availableHeight': this.viewport.height - this.$refs['navigation'].$el.offsetHeight,
            // });

            this.availableHeight = this.viewport.height - this.$refs['navigation'].$el.offsetHeight;

            this.loadingSplashOpen = false;

            if (!this.record) {
                EventBus.$emit('calculatePrices');
            } else {
                this.restoreable = true;
                this.closeExtractor(true);
            }

            EventBus.$on('startBrowserPreloading', this.openBrowserPeloadingSplash);
            EventBus.$on('finishedBrowserPreloading', this.closeBrowserPeloadingSplash);
            EventBus.$on('widthChanged', this.disableAutoWidth);
            EventBus.$on('addLeavingEditorEventListener', this.addLeavingEditorEventListener);
            EventBus.$on('removeLeavingEditorEventListener', this.removeLeavingEditorEventListener);
            EventBus.$on('startIntroDelayedBy', this.startIntroDelayedBy);

            if (this.showIntroVideo) {
                window.setTimeout(() => {
                    this.initIntroVideo();
                }, 1000);
            }
        },

        beforeDestroy() {
            let { beforeDestroy } = App.events;

            if ("function" === typeof beforeDestroy) {
                beforeDestroy();
            }

            window.removeEventListener('resize', this.onResize);

            EventBus.$off('startBrowserPreloading', this.openBrowserPeloadingSplash);
            EventBus.$off('closeBrowserPreloading', this.closeBrowserPeloadingSplash);
            EventBus.$off('widthChanged', this.disableAutoWidth);
            EventBus.$off('addLeavingEditorEventListener', this.addLeavingEditorEventListener);
            EventBus.$off('removeLeavingEditorEventListener', this.removeLeavingEditorEventListener);
            EventBus.$off('startIntroDelayedBy', this.startIntroDelayedBy);

            document.body.style['overscroll-behavior'] = 'unset';
        },

        components: {
            Praise,
            InAppBrowserTutorial,
            AppLoadingSplash,
            AppNavigation,
            AppExtractor,
            AppEditor,
            AppPlexipeopleVisualization,
            AppPriceList,
            AppBase,
            AppBuyButton
        }
    }
</script>

<style>
    @font-face {
        font-family: "icofont";
        src: url("/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/fonts/icofont.eot");
        src: url("/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/fonts/icofont.eot#iefix") format("embedded-opentype"),
        url("/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/fonts/icofont.ttf") format("truetype"),
        url("/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/fonts/icofont.woff") format("woff"),
        url("/custom/plugins/NetiPlexipeopleLeonardo/Resources/frontend/fonts/icofont.svg#icofont") format("svg");
        font-weight: normal;
        font-style: normal;
    }
</style>

<style lang="scss">
    @import '../node_modules/nprogress/nprogress.css';
    @import "assets/scss/utilities/colors";
    @import "assets/scss/components/app";

    .v--modal-overlay {
        .vue-dialog {
            padding: 15px;
            text-align: center;
            margin: auto;
            left: 0 !important;
            max-width: 320px;
            border-radius: 0;

            @media screen and (min-width: $tabletViewportWidth) {
                max-width: 350px;
            }

            .dialog-content {
                padding: 0;

                .dialog-c-title {
                    font-weight: normal;
                    font-size: 1.6rem;
                    color: $highlicht-error;

                    @media screen and (min-width: $tabletViewportWidth) {
                        font-size: 1.8rem;
                    }
                }

                .dialog-c-text {
                    font-size: 1rem;
                    padding-bottom: 15px;

                    @media screen and (min-width: $tabletViewportWidth) {
                        font-size: 1.1rem;
                    }

                    img {
                        margin: 10px auto;
                    }
                }
            }

            .vue-dialog-buttons {
                width: auto;
                margin: auto;
                justify-content: center;
                border: none;

                .vue-dialog-button {
                    padding: 4px 25px 7px;
                    margin: 0 5px;
                    font-size: 0.9rem !important;
                    border-radius: 0;
                    height: auto;
                    min-height: 40px;
                    border: 1px solid $text-color;
                    flex: 0 1 auto !important;

                    @media screen and (min-width: $tabletViewportWidth) {
                        font-size: 1rem !important;
                        padding: 4px 5px 7px;
                        flex: 1 1 100% !important;
                    }

                    &:hover {
                        border: 1px solid $highlicht-error;
                    }

                    &.is--error {
                        color: $highlicht-error;
                    }

                    &.is--success {
                        color: $highlicht-success;
                    }
                }
            }
        }
    }

    .introjs-overlay {
        background: $body-bg;
    }

    .is--leonardo.introjs-fixParent {
        position: absolute;
    }

    .introjs-helperNumberLayer {
        display: none;
    }

    .introjs-helperLayer {
        border-radius: 0;
        background-color: hsla(0,0%,100%,.25);
    }

    .introjs-tooltip {
        border-radius: 0;
        min-width: 260px;
        max-width: 350px;
        margin-top: 5px;
    }

    .introjs-tooltip.introjs-bottom-left-aligned {
        margin-left: 10px;
    }

    .introjs-tooltip.introjs-bottom-right-aligned {
        margin-right: 15px;
    }

    .introjs-tooltip.introjs-bottom-middle-aligned {
        margin-left: 10px;
        margin-right: 10px;
    }

    .introjs-tooltiptext {
        margin-bottom: 10px;
        margin-left: 5px;
    }

    .introjs-tooltiptext span {
        color: $brand-primary;
        font-weight: bold;
    }

    .introjs-arrow.top, .introjs-arrow.top-right,.introjs-arrow.top-middle {
        width: 10px;
        height: 10px;
        transform: rotate(-45deg);
        background: #fff;
        border-color: transparent;
        border-width: 1px 1px 0 0;
        border-style: solid;
        top: -5px;
        /*
          left: 0;
          right: 0;
          width: 5px;
          margin: auto;
        */
    }

    .introjs-button,
    .introjs-button:active,
    .introjs-button:focus {
        padding: 5px 10px;
        background: #fff;
        text-shadow: none;
        border-radius: 0;
        border: none;

        &.introjs-skipbutton {
            background-color: $highlicht-error;
            color: $body-bg;
        }

        &.introjs-donebutton, &.introjs-nextbutton {
            background-color: $brand-third;
            color: $body-bg;
        }
    }

    .introjs-prevbutton {
        display: none;
    }

    .introjs-nextbutton {
        position: absolute;
        right: 10px;
    }

    .introjs-nextbutton.introjs-disabled {
        display: none;
    }

    .introjs-tooltipbuttons {
        text-align: left;
    }

    .introjs-bullets ul li a.active,
    .introjs-bullets ul li a:hover {
        background: $brand-primary;
    }

    .introjs-bullets ul li a {
        width: 4px;
        height: 4px;
    }

    .introjs-bullets ul li {
        float: none;
        margin: 0 3px;
        display: inline-block;
    }

    .introjs-bullets ul {
        position: absolute;
        bottom: 5px;
        right: 0;
        left: 0;
        margin: auto;
    }
</style>



// WEBPACK FOOTER //
// src/App.vue